import React, { useState, useEffect } from "react";
import { sanitizeInput } from "./utils/sanitize";
import { useTranslation } from "./contexts/LocaleContext";
import {
  Pipette,
  Trash2,
  AlertTriangle,
  Package,
  Search,
  DollarSign,
  Hash,
  Type,
} from "lucide-react";
import { Item } from "./types";

type ItemsDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  savedItems: Item[];
  onAddItem: (item: Omit<Item, "id">) => void | Promise<void>;
  onUpdateItem: (
    id: string,
    item: Omit<Item, "id">
  ) => void | Promise<void>;
  onDeleteItem: (id: string) => void | Promise<void>;
  initialData?: Partial<Omit<Item, "id">>;
};

export default function ItemsDialog({
  isOpen,
  onClose,
  savedItems,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
  initialData,
}: ItemsDialogProps) {
  const t = useTranslation();
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [formData, setFormData] = useState({
    description: "",
    amount: "" as string | number,
    quantity: 1,
    unit: "",
    pricePerUnit: "" as string | number,
  });

  const [searchQuery, setSearchQuery] = useState("");

  const filteredItems = savedItems.filter((item) => {
    const query = searchQuery.toLowerCase();
    return (
      item.description.toLowerCase().includes(query) ||
      (item.unit && item.unit.toLowerCase().includes(query))
    );
  });

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      // Pre-fill form with initialData if not editing
      if (!editingItem && initialData) {
        setFormData({
          description: initialData.description || "",
          amount: initialData.amount === 0 ? "" : initialData.amount || "",
          quantity: initialData.quantity || 1,
          unit: initialData.unit || "",
          pricePerUnit: (initialData as any).pricePerUnit === 0 ? "" : (initialData as any).pricePerUnit || "",
        });
      }
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, initialData, editingItem]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid()) {
      return;
    }

    try {
      const finalData = {
        ...formData,
        amount: typeof formData.amount === "string" ? parseFloat(formData.amount) || 0 : formData.amount,
        pricePerUnit: typeof formData.pricePerUnit === "string" ? parseFloat(formData.pricePerUnit) || 0 : formData.pricePerUnit,
      };

      if (editingItem) {
        await onUpdateItem(editingItem.id, finalData);
      } else {
        await onAddItem(finalData);
      }
      handleCancel();
    } catch (error) {
      console.error("Error submitting item form:", error);
    }
  };

  const handleEdit = (item: Item) => {
    setEditingItem(item);
    setFormData({
      description: item.description,
      amount: item.amount === 0 ? "" : item.amount,
      quantity: item.quantity || 1,
      unit: item.unit || "",
      pricePerUnit: (item as any).pricePerUnit === 0 ? "" : (item as any).pricePerUnit,
    });
  };

  const handleCancel = () => {
    setEditingItem(null);
    if (initialData) {
      setFormData({
        description: initialData.description || "",
        amount: initialData.amount === 0 ? "" : initialData.amount || "",
        quantity: initialData.quantity || 1,
        unit: initialData.unit || "",
        pricePerUnit: (initialData as any).pricePerUnit === 0 ? "" : (initialData as any).pricePerUnit || "",
      });
    } else {
      setFormData({
        description: "",
        amount: "",
        quantity: 1,
        unit: "",
        pricePerUnit: "",
      });
    }
  };

  const handleDelete = (id: string) => {
    if (
      window.confirm(
        "Are you sure you want to delete this item?"
      )
    ) {
      onDeleteItem(id);
    }
  };

  const isFormValid = () => {
    return formData.description.trim() !== "";
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[200] p-4"
      onClick={handleBackdropClick}
      onMouseDown={(e) => {
        if (e.target !== e.currentTarget) {
          e.stopPropagation();
        }
      }}
    >
      <div
        className="bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl max-w-4xl w-full max-h-[calc(100vh-80px)] sm:max-h-[90vh] overflow-hidden border border-slate-200/50 dark:border-slate-800 transition-colors flex flex-col mt-[64px] sm:mt-0"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2 sm:p-6 border-b border-slate-100 dark:border-slate-800">
          <div>
            <h2 className="text-lg sm:text-xl font-black text-slate-900 dark:text-slate-100">
              Manage saved items
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 sm:p-2 bg-slate-50 dark:bg-slate-800 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 rounded-xl transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-4 h-4 sm:w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-4 sm:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Left: Form */}
            <div className="space-y-6">
              <div>
                <h3 className="text-base font-black text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                  <div className="w-1.5 h-5 bg-indigo-600 rounded-full" />
                  {editingItem ? "Edit item" : "Add new item"}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-3">
                    <div className="relative">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 block">
                        Description
                      </label>
                      <div className="relative">
                        <Type className="absolute left-4 top-2.5 h-4 w-4 text-slate-400" />
                        <input
                          type="text"
                          value={formData.description}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              description: sanitizeInput(e.target.value),
                            }))
                          }
                          placeholder="e.g. Web Development"
                          className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all text-sm"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 block">
                          Default Price
                        </label>
                        <div className="relative">
                          <DollarSign className="absolute left-4 top-2.5 h-4 w-4 text-slate-400" />
                          <input
                            type="text"
                            inputMode="decimal"
                            value={formData.amount}
                            onChange={(e) => {
                              const val = e.target.value.replace(",", ".");
                              if (val === "" || /^\d*\.?\d*$/.test(val)) {
                                setFormData((prev) => ({
                                  ...prev,
                                  amount: val,
                                }));
                              }
                            }}
                            placeholder="0.00"
                            className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all text-sm"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 block">
                          Unit Amount
                        </label>
                        <div className="relative">
                          <Hash className="absolute left-4 top-2.5 h-4 w-4 text-slate-400" />
                          <input
                            type="text"
                            inputMode="decimal"
                            value={formData.pricePerUnit}
                            onChange={(e) => {
                              const val = e.target.value.replace(",", ".");
                              if (val === "" || /^\d*\.?\d*$/.test(val)) {
                                setFormData((prev) => ({
                                  ...prev,
                                  pricePerUnit: val,
                                }));
                              }
                            }}
                            placeholder="0.00"
                            className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all text-sm"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 block">
                          Default Unit
                        </label>
                        <div className="relative">
                          <Package className="absolute left-4 top-2.5 h-4 w-4 text-slate-400" />
                          <input
                            type="text"
                            value={formData.unit}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                unit: sanitizeInput(e.target.value),
                              }))
                            }
                            placeholder="pcs, hours, etc."
                            className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {!isFormValid() && (
                    <div className="px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/30 rounded-xl flex items-center gap-2 text-amber-800 dark:text-amber-400 text-[10px] font-bold animate-pulse">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                      <span>Description is required</span>
                    </div>
                  )}

                  <div className="flex gap-2 pt-2">
                    <button
                      type="submit"
                      disabled={!isFormValid()}
                      className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 dark:disabled:bg-slate-800 text-white rounded-xl font-black transition-all shadow-md shadow-indigo-200 dark:shadow-none text-sm"
                    >
                      {editingItem ? "Update item" : "Add item"}
                    </button>
                    {editingItem && (
                      <button
                        type="button"
                        onClick={handleCancel}
                        className="px-4 py-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-xl font-black transition-all text-sm"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* Right: List */}
            <div className="space-y-4">
              <div className="flex flex-col gap-4">
                <h3 className="text-base font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <div className="w-1.5 h-5 bg-slate-200 dark:bg-slate-700 rounded-full" />
                  Existing items
                </h3>
                
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search items..."
                    className="w-full pl-10 pr-4 py-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all text-sm"
                  />
                </div>
              </div>

              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                {filteredItems.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border-2 border-dashed border-slate-100 dark:border-slate-800">
                    <Package className="w-10 h-10 text-slate-200 dark:text-slate-700 mx-auto mb-2" />
                    <p className="text-slate-400 dark:text-slate-500 italic text-xs font-medium">
                      {searchQuery ? "No matching items found" : "No saved items yet"}
                    </p>
                  </div>
                ) : (
                  filteredItems.map((item, idx) => (
                    <div
                      key={item.id || `saved-item-${idx}`}
                      className={`group p-4 rounded-2xl border-2 transition-all ${
                        editingItem?.id === item.id
                          ? "border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20"
                          : "border-slate-50 dark:border-slate-800 hover:border-slate-200 dark:hover:border-slate-700"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 space-y-0.5">
                          <div className="flex items-center gap-2">
                            <p className="font-black text-sm text-slate-900 dark:text-slate-100 truncate">
                              {item.description}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <p className="text-[10px] text-slate-500 font-medium">
                              Price: <span className="text-indigo-600 dark:text-indigo-400 font-bold">{item.amount}</span>
                            </p>
                            {item.unit && (
                              <p className="text-[10px] text-slate-500 font-medium">
                                Unit: <span className="text-slate-700 dark:text-slate-300 font-bold">{item.unit}</span>
                              </p>
                            )}
                            {(item as any).pricePerUnit > 0 && (
                              <p className="text-[10px] text-slate-500 font-medium">
                                Unit Amount: <span className="text-emerald-600 dark:text-emerald-400 font-bold">{(item as any).pricePerUnit}</span>
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity flex-shrink-0">
                          <button
                            onClick={() => handleEdit(item)}
                            className="p-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-100 transition-colors"
                            title="Edit"
                          >
                            <Pipette className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="p-2 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-100 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
